import {LoadProfile, RateCalculator} from '@bellawatt/electric-rate-engine';
import {useInputs} from '@bellawatt/use-inputs';
import {YEAR} from '@generic/data/assumptions';
import {getRates} from '@generic/data/rates';
import calcScaledFacilityLoadProfile from '@generic/functions/calcScaledFacilityLoadProfile';
import calcVehicleSetFacilityLoadProfile from '@generic/functions/calcVehicleSetFacilityLoadProfile';
import ChargingSiteLoadProfile from '@generic/pages/ChargingCalculator/ChargingSiteLoadProfile';
import ChargingSiteMonthlyDemand from '@generic/pages/ChargingCalculator/ChargingSiteMonthlyDemand';
import ElectricityBill from '@generic/pages/ChargingCalculator/ElectricityBill';
import RateBreakdownSelector from '@generic/pages/ChargingCalculator/RateBreakdownSelector';
import RateChargePanel from '@generic/pages/ChargingCalculator/RateChargePanel';
import RateComponentCategoryChart from '@generic/pages/ChargingCalculator/RateComponentCategoryChart';
import SupportingCopyPrintout from '@generic/pages/ChargingCalculator/SupportingCopyPrintout';
import {Box, Grid, makeStyles} from '@material-ui/core';
import React, {useState} from 'react';
import EVPhaseInRateDisclaimer from '../../components/EVPhaseInRateDisclaimer';
import SmartChargeCommercialBreakdown from '../../components/SmartChargeCommercialBreakdown';

const useStyles = makeStyles(() => ({
  flexedGridItem: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

export default function ChargingRateBreakdown({annualCosts}) {
  const inputs = useInputs();
  const {separatelyMetered} = inputs;
  RateCalculator.shouldValidate = false;

  const classes = useStyles();

  const evLoadProfileData = calcVehicleSetFacilityLoadProfile(inputs);
  const evLoadProfile = new LoadProfile(evLoadProfileData, {year: YEAR});
  const totalLoadProfile = separatelyMetered
    ? evLoadProfile
    : evLoadProfile.aggregate(calcScaledFacilityLoadProfile(inputs));

  const maxDemand = totalLoadProfile.max();

  const rates = getRates(inputs, maxDemand);

  const [rate, setRate] = useState(rates[0]);
  const [selectedRateComponent, setSelectedRateComponent] = useState('energy');

  const handleChange = (rateName) => {
    setRate(rates.find((r) => r.name === rateName) || rates[0]);
  };

  const selectedAnnualCosts = annualCosts.find((c) => c.name === rate.name) || annualCosts[0];
  const {fleetSccRebate} = selectedAnnualCosts;

  const rateCalculator = new RateCalculator({
    ...rate,
    loadProfile: totalLoadProfile,
  });

  return (
    <Box mt={5}>
      <Box mb={5}>
        <Grid container spacing={2} mb={5}>
          <Grid item xs={12} lg={6}>
            <RateBreakdownSelector
              rateName={rate.name}
              handleChange={handleChange}
              maxDemand={maxDemand}
            />

            <SupportingCopyPrintout rateName={rate.name} />
          </Grid>
          <Grid item xs={12} lg={6} className={classes.flexedGridItem}>
            <SmartChargeCommercialBreakdown />
          </Grid>
        </Grid>
      </Box>
      <Grid container>
        <Grid item xs={12} lg={6}>
          <RateComponentCategoryChart
            rateCalculator={rateCalculator}
            onHover={setSelectedRateComponent}
            sccRebate={fleetSccRebate}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <Box px={10} display="flex" justifyContent="center">
            <RateChargePanel
              rateCalculator={rateCalculator}
              loadProfile={totalLoadProfile}
              rateTitle={rate.title}
              rateComponent={selectedRateComponent}
              sccRebate={fleetSccRebate}
            />
          </Box>
        </Grid>
      </Grid>

      <ChargingSiteMonthlyDemand />
      <ChargingSiteLoadProfile />
      <ElectricityBill
        rateCalculator={rateCalculator}
        loadProfile={totalLoadProfile}
        rateTitle={rate.title}
      />

      <Box mb={5}>
        <EVPhaseInRateDisclaimer rateCalculator={rateCalculator} />
      </Box>
    </Box>
  );
}
